import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Component, OnInit, OnDestroy } from '@angular/core';
import moment from 'moment';

import { TrackingService } from '@app/core/services/tracking.service';
import { ConfigureService } from '../../../services/configure.service';
import { ToastService } from '@app/core/services/toast.service';

import { FormHelper } from '@app/ui-forms/classes/form-helper';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FormInputEvent } from '@app/ui-forms/models/form-input-event.model';

@Component({
	selector: 'cng-configure-collection-form',
	templateUrl: './collection-form.component.html',
	styleUrls: ['./collection-form.component.less'],
})
export class ConfigureCollectionFormComponent implements OnInit, OnDestroy {
	collection_form: FormGroup;
	collection_time_options = [
		{ value: '' },
		{ value: '10:00', name: 'Morning' },
		{ value: '14:00', name: 'Afternoon' },
	];
	unavailable_days: string[] = this.configure_service.configure_data.steps.delivery_options.data.collection.unavailable_days;
	unavailable_dates: string[] = this.configure_service.configure_data.steps.delivery_options.data.collection.unavailable_dates;
	submitting: boolean = false;
	lead_days: number = this.configure_service.configure_data.steps.delivery_options.data.collection.lead_days;
	collection_date_min: string = moment().add(this.lead_days, 'days').format('yyyy-MM-DD');
	tracking_log: string[] = [];
	collection_location_options: {
		value: number | string;
		name: string;
		description?: string;
	}[] = this.getCollectionLocations();
	destroyed$: Subject<boolean> = new Subject<boolean>();

	constructor(
        private configure_service: ConfigureService,
        private toast_service: ToastService,
        private tracking_service: TrackingService
    ) {
		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.collection_location_options = this.getCollectionLocations();
			this.collection_form = this.getForm();
		});
	}

	ngOnInit(): void {
		this.collection_form = this.getForm();
	}

	getCollectionLocations() {
		return this.configure_service.configure_data.steps.delivery_options.data.collection.locations.map(
			(location) => {
				return {
					value: location.id,
					name: location.name,
					description: location.address_string,
				};
			}
		);
	}

	getForm() {
		return new FormGroup({
			collection_location_id: new FormControl(
				this.collection_location_options.length === 1 ? this.collection_location_options[0].value : null
			),
			collection_date: new FormControl('', Validators.required),
			collection_time: new FormControl('', Validators.required),
		});
	}

	onSubmit(): void {
		if (this.submitting) {
			return;
		}

		this.tracking_service.trackFormSubmitAttempt(
			'collection_form',
			FormHelper.getCompletedControls(this.collection_form),
			FormHelper.getEmptyControls(this.collection_form)
		);
		if (this.collection_form.invalid) {
			FormHelper.forceValidate(this.collection_form);
			this.tracking_service.trackFormValidationFailure(
				'collection_form',
				'invalid_fields[' + FormHelper.getInvalidControls(this.collection_form).join(',') + ']'
			);
			this.toast_service.error('Form has invalid fields');
		} else {
			this.tracking_service.trackFormValidationPass('collection_form');
			this.submitting = true;

			const collection_body = {
				collection_date:
					this.collection_form.value.collection_date + ' ' + this.collection_form.value.collection_time,
				collection_location_id: this.collection_form.value.collection_location_id,
			};

			this.configure_service.saveCollection(collection_body).subscribe((response) => {
				this.submitting = false;
				if (response) {
					this.toast_service.success('Your collection request was sent successfully');
					this.tracking_service.trackFormSubmitSuccess('collection_form');
					this.configure_service
						.track('collection.requested', {
							collection_date: collection_body.collection_date,
							collection_location_id: collection_body.collection_location_id,
						})
						.subscribe();
				} else {
					this.tracking_service.trackFormSubmitFailure('collection_form');
					this.toast_service.error('An error occurred when submitting your request');
				}
			});
		}
	}

	onFormInput(form: any): void {
		const event: FormInputEvent = FormHelper.getInputEvent(form);
		if (this.tracking_log.indexOf(event.log_name) === -1) {
			this.tracking_service.trackFormInput(event.form_name, event.input_type, event.input_name);
			this.tracking_log.push(event.log_name);
		}
	}

	onDatePickerInput(): void {
		const log_name: string = `collection_form__date_picker__collection_date`;
		if (this.tracking_log.indexOf(log_name) === -1) {
			this.tracking_service.trackFormInput('configure_collection_form', 'date_picker', 'collection_date');
			this.tracking_log.push(log_name);
		}
	}

	onRadioButtonInput(): void {
		const log_name: string = `collection_form__radio_button__collection_location_id`;
		if (this.tracking_log.indexOf(log_name) === -1) {
			this.tracking_service.trackFormInput('configure_collection_form', 'radio_button', 'collection_location_id');
			this.tracking_log.push(log_name);
		}
	}
	ngOnDestroy(): void {
		this.destroyed$.next(true);
	}
}
